import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Clock, Users, Shield, ArrowRight, X, ChevronDown } from 'lucide-react';
import { useCurrency, formatPrice } from '../hooks/useCurrency';

const SESSIONS = [
  { label: '30 minutes', value: '30min', eur: 6, inr: 300 },
  { label: '1 hour', value: '1h', eur: 12, inr: 600 },
  { label: '5 hours', value: '5h', eur: 55, inr: 2700 },
  { label: '10 hours', value: '10h', eur: 100, inr: 5000 },
  { label: '20 hours', value: '20h', eur: 180, inr: 9000 },
];

const PACKS = [
  { name: 'Starter Session', duration: '30 min', eur: 6, inr: 300, desc: 'A simple first step to start speaking.' },
  { name: 'Single Session', duration: '1 hour', eur: 12, inr: 600, desc: 'Focused speaking practice and feedback.' },
  { name: 'Conversation Pack', duration: '5 hours', eur: 55, inr: 2700, desc: 'Build consistency and confidence.' },
  { name: 'Progress Pack', duration: '10 hours', eur: 100, inr: 5000, desc: 'Structured improvement and fluency.' },
  { name: 'Commitment Pack', duration: '20 hours', eur: 180, inr: 9000, desc: 'Long-term progress and mastery.' },
];

const TIME_ZONES = ['UTC', 'GMT', 'CET', 'IST', 'EST', 'PST'];
const TIME_PREFS = ['Morning', 'Afternoon', 'Evening'];

function CurrencyToggle({ currency, setCurrency }) {
  return (
    <div className="inline-flex items-center gap-2" data-testid="currency-toggle">
      <span className="text-xs text-[#6B4E37]">Currency</span>
      <div className="flex bg-white border border-[#E8E4DC] rounded-full p-0.5">
        <button
          onClick={() => setCurrency('EUR')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
            currency === 'EUR'
              ? 'bg-[#1A2F4A] text-white'
              : 'text-[#6B4E37] hover:text-[#1A2F4A]'
          }`}
          data-testid="currency-eur">
          EUR (€)
        </button>
        <button
          onClick={() => setCurrency('INR')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
            currency === 'INR'
              ? 'bg-[#1A2F4A] text-white'
              : 'text-[#6B4E37] hover:text-[#1A2F4A]'
          }`}
          data-testid="currency-inr">
          INR (₹)
        </button>
      </div>
    </div>
  );
}

function BookingModal({ open, onClose, currency }) {
  const [form, setForm] = useState({ session: '30min', time: 'Morning', timezone: 'CET', email: '' });
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-8" data-testid="booking-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#999] hover:text-[#1A2F4A] transition-colors" data-testid="booking-modal-close">
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="text-center py-6" data-testid="booking-success">
            <div className="w-14 h-14 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="text-lg font-bold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Demande envoyée
            </h3>
            <p className="text-sm text-[#6B4E37]">Your request has been received. We will contact you shortly.</p>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-bold text-[#1A2F4A] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Réserver une session
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">Session Type</label>
                <div className="relative">
                  <select
                    value={form.session}
                    onChange={set('session')}
                    className="w-full appearance-none px-4 py-2.5 border border-[#E8E4DC] rounded-xl text-sm text-[#1A2F4A] bg-[#FAFAF8] focus:outline-none focus:border-[#D4AF37]"
                    data-testid="booking-session-type">
                    {SESSIONS.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label} — {formatPrice(o.eur, o.inr, currency)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 text-[#999] absolute right-3 top-3 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">Preferred Time</label>
                <div className="relative">
                  <select
                    value={form.time}
                    onChange={set('time')}
                    className="w-full appearance-none px-4 py-2.5 border border-[#E8E4DC] rounded-xl text-sm text-[#1A2F4A] bg-[#FAFAF8] focus:outline-none focus:border-[#D4AF37]"
                    data-testid="booking-time">
                    {TIME_PREFS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 text-[#999] absolute right-3 top-3 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">Time Zone</label>
                <div className="relative">
                  <select
                    value={form.timezone}
                    onChange={set('timezone')}
                    className="w-full appearance-none px-4 py-2.5 border border-[#E8E4DC] rounded-xl text-sm text-[#1A2F4A] bg-[#FAFAF8] focus:outline-none focus:border-[#D4AF37]"
                    data-testid="booking-timezone">
                    {TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 text-[#999] absolute right-3 top-3 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-2.5 border border-[#E8E4DC] rounded-xl text-sm text-[#1A2F4A] bg-[#FAFAF8] focus:outline-none focus:border-[#D4AF37]"
                  data-testid="booking-email"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-[#1A2F4A] hover:bg-[#243a58] text-white py-3 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                data-testid="booking-submit">
                Envoyer la demande
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function SupportTeacher() {
  const [showBooking, setShowBooking] = useState(false);
  const { currency, setCurrency } = useCurrency();

  const openBooking = () => setShowBooking(true);

  return (
    <div className="min-h-screen bg-[#FAF7F0]">

      {/* Section 1: Header */}
      <section className="pt-20 pb-14" data-testid="support-header">
        <div className="max-w-[700px] mx-auto px-8 text-center">
          <h1
            className="text-4xl sm:text-5xl font-bold text-[#1A2F4A] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="support-title">
            Support a Teacher
          </h1>
          <div className="flex items-center justify-center gap-4 mb-5">
            <div className="h-px w-12 bg-[#D4AF37]/50"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/50"></div>
            <div className="h-px w-12 bg-[#D4AF37]/50"></div>
          </div>
          <p className="text-base text-[#6B4E37] leading-relaxed">
            Practice real French with a teacher while supporting the people behind this platform.
          </p>
          <p className="text-sm text-[#999] mt-3 italic" data-testid="support-guidance">
            This is the fastest way to improve your spoken French.
          </p>
          <div className="mt-5">
            <CurrencyToggle currency={currency} setCurrency={setCurrency} />
          </div>
        </div>
      </section>

      {/* Section 2: Quick Session Selection */}
      <section className="pb-14" data-testid="session-selection">
        <div className="max-w-[700px] mx-auto px-8">
          <h2
            className="text-xl font-bold text-[#1A2F4A] text-center mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Choose your session
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            {SESSIONS.map((opt) => (
              <div
                key={opt.value}
                className="bg-white border border-[#E8E4DC] rounded-xl p-4 text-center hover:border-[#D4AF37] transition-colors"
                data-testid={`session-card-${opt.value}`}>
                <p className="text-sm font-semibold text-[#1A2F4A] mb-1">{opt.label}</p>
                <p className="text-lg font-bold text-[#D4AF37]" data-testid={`price-${opt.value}`}>
                  {formatPrice(opt.eur, opt.inr, currency)}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button
              onClick={openBooking}
              className="bg-[#1A2F4A] hover:bg-[#243a58] text-white px-8 py-3 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2"
              data-testid="cta-reserver-1">
              Réserver une session
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-[#999] mt-3 max-w-sm mx-auto">
              After submitting your request, we will contact you via email or WhatsApp to confirm your session.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[400px] mx-auto"><div className="h-px bg-[#E8E4DC]"></div></div>

      {/* Section 3: What happens in a session */}
      <section className="py-14" data-testid="what-happens">
        <div className="max-w-[600px] mx-auto px-8">
          <h2
            className="text-xl font-bold text-[#1A2F4A] text-center mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            What happens in a session?
          </h2>
          <div className="space-y-3">
            {[
              { icon: Users, text: 'Live one-to-one conversation' },
              { icon: CheckCircle, text: 'Personalized feedback and correction' },
              { icon: Shield, text: 'Adapted to your level and goals' },
              { icon: Clock, text: 'Safe, judgment-free speaking environment' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-[#E8E4DC] rounded-xl px-5 py-3.5">
                <item.icon className="w-4.5 h-4.5 text-[#D4AF37] flex-shrink-0" />
                <p className="text-sm text-[#1A2F4A]">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: Why paid sessions */}
      <section className="pb-14" data-testid="why-paid">
        <div className="max-w-[550px] mx-auto px-8 text-center">
          <h2
            className="text-xl font-bold text-[#1A2F4A] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Why paid sessions?
          </h2>
          <p className="text-sm text-[#6B4E37] leading-relaxed mb-2">
            All learning content on this platform is completely free.
          </p>
          <p className="text-sm text-[#6B4E37] leading-relaxed">
            These sessions exist to support teachers and provide real human interaction beyond AI.
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[400px] mx-auto"><div className="h-px bg-[#E8E4DC]"></div></div>

      {/* Section 5: Session Options */}
      <section className="py-14" data-testid="session-options">
        <div className="max-w-[650px] mx-auto px-8">
          <h2
            className="text-xl font-bold text-[#1A2F4A] text-center mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Session options
          </h2>
          <div className="space-y-4">
            {PACKS.map((pack, i) => (
              <div key={i} className="bg-white border border-[#E8E4DC] rounded-xl px-6 py-5" data-testid={`pack-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-[#1A2F4A] text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {pack.name}
                  </h3>
                  <span className="text-xs font-semibold text-[#D4AF37]">
                    {pack.duration} — {formatPrice(pack.eur, pack.inr, currency)}
                  </span>
                </div>
                <p className="text-sm text-[#6B4E37]">{pack.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <button
              onClick={openBooking}
              className="bg-[#1A2F4A] hover:bg-[#243a58] text-white px-8 py-3 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2"
              data-testid="cta-reserver-2">
              Réserver une session
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Section 6: Teacher Trust */}
      <section className="pb-14" data-testid="teacher-trust">
        <div className="max-w-[550px] mx-auto px-8 text-center">
          <p className="text-sm text-[#6B4E37] italic leading-relaxed">
            Sessions are conducted by qualified French teachers trained in conversation-based pedagogy.
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[400px] mx-auto"><div className="h-px bg-[#E8E4DC]"></div></div>

      {/* Section 7: Session Details */}
      <section className="py-14" data-testid="session-details">
        <div className="max-w-[500px] mx-auto px-8">
          <h2
            className="text-xl font-bold text-[#1A2F4A] text-center mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Session details
          </h2>
          <ul className="space-y-2.5">
            {[
              'Valid for 12 months',
              'Sessions can be used in 30-minute or 1-hour formats',
              'Easy rescheduling available',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#6B4E37]">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 8: Final Message */}
      <section className="pb-20" data-testid="final-message">
        <div className="max-w-[550px] mx-auto px-8 text-center">
          <p className="text-base text-[#1A2F4A] font-semibold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
            Learning French here is free.
          </p>
          <p className="text-base text-[#6B4E37] mb-6">
            Supporting teachers keeps it human.
          </p>
          <button
            onClick={openBooking}
            className="bg-[#D4AF37] hover:bg-[#C9A961] text-[#1A2F4A] px-8 py-3 rounded-full text-sm font-bold transition-colors inline-flex items-center gap-2"
            data-testid="cta-reserver-3">
            Réserver une session
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-[#E8E4DC] py-10">
        <div className="max-w-[700px] mx-auto px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-lg font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>SELORA</span>
            <span className="text-xs text-[#6B4E37]">French Academy</span>
          </Link>
          <p className="text-xs text-[#999]">© 2026 Selora French Academy</p>
        </div>
      </footer>

      {/* Booking Modal */}
      <BookingModal open={showBooking} onClose={() => setShowBooking(false)} currency={currency} />
    </div>
  );
}
